import * as changeCase from 'change-case';
import { Transformer } from '@cp/ds/src/components/form';

export enum StringCase {
  NoCase = 'noCase',
  CamelCase = 'camelCase',
  PascalCase = 'pascalCase',
  PascalSnakeCase = 'pascalSnakeCase',
  CapitalCase = 'capitalCase',
  ConstantCase = 'constantCase',
  DotCase = 'dotCase',
  KebabCase = 'kebabCase',
  PathCase = 'pathCase',
  SentenceCase = 'sentenceCase',
  SnakeCase = 'snakeCase',
  TrainCase = 'trainCase',
}

/**
 * Configuration options for StringTransformer.
 */
interface StringTransformerOptions {
  /**
   * Text case transformation to apply.
   * See available cases: https://github.com/blakeembrey/change-case/tree/main/packages/change-case#built-in-methods
   * Examples: 'camelCase', 'snakeCase', 'pascalCase', 'kebabCase', etc.
   */
  textCase?: StringCase;
  /**
   * Whether to allow separators in the transformed string.
   * If true, seoarators will be preserved along with alphabetic characters.
   * If false (default), only alphabetic characters will be kept.
   */
  enableSeparators?: boolean;
}

/**
 * A transformer for string values that applies text case transformations and character filtering.
 *
 * This transformer can:
 * - Apply various text case transformations (camelCase, snakeCase, etc.) using the change-case library
 * - Filter out non-alphabetic characters (supports English and Cyrillic alphabets)
 * - Optionally preserve spaces in the input string
 *
 * @example
 * ```ts
 * // Transform to camelCase and remove non-alphabetic characters
 * const transformer = new StringTransformer({ textCase: 'camelCase' });
 * transformer.fromControl('hello world!'); // 'helloWorld'
 *
 * // Preserve spaces
 * const transformerWithSpaces = new StringTransformer({
 *   textCase: 'capitalCase',
 *   enableSpace: true
 * });
 * transformerWithSpaces.fromControl('hello world!'); // 'Hello World'
 * ```
 *
 * @see {@link https://github.com/blakeembrey/change-case/tree/main/packages/change-case#built-in-methods | change-case documentation}
 */
export class StringTransformer implements Transformer<string, string> {
  #options: StringTransformerOptions = {
    textCase: StringCase.NoCase,
    enableSeparators: false,
  };

  constructor(options?: StringTransformerOptions) {
    this.#options = { ...this.#options, ...options };
  }

  private withRegex = (str: string): string => {
    if (this.#options.enableSeparators) {
      return str.replace(/[^a-zA-Zа-яА-ЯёЁ _\-.\/]/g, '');
    }
    return str.replace(/[^a-zA-Zа-яА-ЯёЁ]/g, '');
  };

  private withCase = (str: string) => {
    if (this.#options.textCase) {
      // eslint-disable-next-line import/namespace
      const toCase = changeCase[this.#options.textCase as keyof typeof changeCase];
      return toCase(str) as string;
    }
    return str;
  };

  private compose = (str: string, ...args: Array<(str: string) => string>) => {
    let value = str;
    for (const combine of args) {
      value = combine(value);
    }
    return value;
  };

  fromControl(value: string = '') {
    return this.compose(value, this.withCase, this.withRegex).trim();
  }

  toControl = this.fromControl;
}
